/**
 * SlidesEditor Component
 *
 * A comprehensive slide management interface that allows users to create, edit, and organize
 * presentation slides. This component serves as the main editor for presentation projects.
 *
 * Core Features:
 * - Slide Management: Add, remove, and reorder slides
 * - Template Integration: Associate slides with PowerPoint templates
 * - Content Editing: Edit slide descriptions and content
 * - AI Integration: Generate and suggest content using AI
 * - Preview: Visual preview of slides with template boundaries
 *
 * The component manages two key concepts:
 * - slideNumber: 1-based index representing the slide's position in the presentation
 * - slideIndex: 0-based index representing the slide's position in the editingSlides array
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome,
  Build,
  Clear as ClearIcon,
  Edit as EditIcon,
  ErrorOutline,
  ExpandMore,
  HelpOutline,
  Image as ImageIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Warning,
} from '@mui/icons-material';
import { Slide, Project, ParsedSlide, Image } from '../../types';
import { useLang } from '../../contexts/langContext';
import { usePptxFile } from '../../hooks/usePptxFile';
import { useSlideManagement } from '../../hooks/useSlideManagement';
import { useSetState } from '../../hooks/useSetState';
import { useBranding } from '../../contexts/BrandingContext';
import { magicButtonProps } from '../common/ai';
import { getUnassignedSlides, getSlidesMissingDescription, createNewSlide, isElementLocked } from '../../utils/slideUtils';
import { processPromisesWithConcurrency } from '../../utils/concurrency';
import BasicEditDialog from './BasicEditDialog';
import SlidePreviewWithBoundaries from '../../components/pptxFiles/SlidePreviewWithBoundaries';
import SlideActions from './SlideActions';
import SlideElementList from './SlideElementList';
import TextEditorDialog from './TextEditorDialog';
import GenerateContentDialog from './dialogs/GenerateContentDialog';
import NewSlideDialog from './dialogs/NewSlideDialog';
import DeleteConfirmationDialog from './dialogs/DeleteConfirmationDialog';
import ChangeTemplateDialog from './dialogs/ChangeTemplateDialog';
import ImagePickerContainer from '../images/ImagePickerContainer';
import ImageGenerationDialog from '../images/ImageGenerationDialog';

// Pre-defined instruction options
const preDefinedInstructions = [
  { en: 'Rewrite', zh: '重写' },
  { en: 'Expand', zh: '扩写' },
  { en: 'Summarize', zh: '缩写' },
  { en: 'Formal tone', zh: '正式语气' },
  { en: 'Casual tone', zh: '随意语气' },
  { en: 'Add details', zh: '添加细节' },
  { en: 'Add examples', zh: '添加例子' },
  { en: 'Add statistics', zh: '添加统计数据' },
  { en: 'Add quotes', zh: '添加引用' },
];

interface SlidesEditorProps {
  slides: Slide[];
  onUpdateSlides: (updatedSlides: Slide[]) => void;
  onSuggestPptxSlideNumber: (editingSlideNumber: number) => Promise<{ slideNumber: number | null | undefined; reason?: string }>;
  onSuggestPptxSlideNumbers?: (slideNumbers: number[]) => Promise<number[]>;
  onSuggestSlideData: (slideIndex: number, instruction?: string, fileIds?: string[]) => Promise<any>;
  onSuggestSlideElementValue: (
    slideIndex: number,
    elementName: string,
    elementType: 'text' | 'picture' | 'table' | 'chart',
    instruction?: string,
    fileIds?: string[]
  ) => Promise<string>;
  onRecommendSlideDescription?: (slideIndex: number) => Promise<any>;
  project: Project;
}

const SlidesEditor: React.FC<SlidesEditorProps> = ({
  slides,
  onUpdateSlides,
  onSuggestPptxSlideNumber,
  onSuggestPptxSlideNumbers,
  onSuggestSlideData,
  onSuggestSlideElementValue,
  onRecommendSlideDescription,
  project,
}) => {
  const { lang } = useLang();
  const { pptxFile } = usePptxFile(project.pptxFileId);

  // Use our new hooks for state management
  const { editingSlides, setEditingSlides, moveSlide, addSlide, removeSlide } = useSlideManagement(slides, onUpdateSlides);
  const [currentSelectedSlideIndex, setCurrentSelectedSlideIndex] = useState<number | null>(null);
  const { state: editingDescriptionSlideIndices, addItem: addEditingDescription, removeItem: removeEditingDescription } = useSetState<number>();
  const {
    state: slidesWithSuggestingPptxSlideNumber,
    addItem: addSuggestingPptxSlideNumber,
    removeItem: removeSuggestingPptxSlideNumber,
  } = useSetState<number>();
  const { state: slidesWithSuggestingSlideData, addItem: addSuggestingSlideData, removeItem: removeSuggestingSlideData } = useSetState<number>();
  const {
    state: slidesWithGeneratingDescription,
    addItem: addGeneratingDescription,
    removeItem: removeGeneratingDescription,
  } = useSetState<number>();
  const [pickingTemplateForSlideIndex, setPickingTemplateForSlideIndex] = useState<number | null>(null);
  const { state: elementSuggestingStates, addItem: addElementSuggesting, removeItem: removeElementSuggesting } = useSetState<string>();
  const [hoveredElement, setHoveredElement] = useState<{ slideIndex: number; elementIndex: string } | null>(null);
  const [newSlideDialogOpen, setNewSlideDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; slideIndex: number | null }>({
    open: false,
    slideIndex: null,
  });
  const [addSlideTarget, setAddSlideTarget] = useState<{ position: 'above' | 'below'; index: number } | null>(null);
  const [fixingUnassignedSlides, setFixingUnassignedSlides] = useState<{ isFixing: boolean; progress: number; total: number }>({
    isFixing: false,
    progress: 0,
    total: 0,
  });
  const [generateContentDialog, setGenerateContentDialog] = useState<{
    open: boolean;
    slideIndex: number;
    elementIndex?: number;
    elementName?: string;
    elementType?: 'text' | 'picture' | 'table' | 'chart';
    anchorEl: HTMLElement | null;
  }>({
    open: false,
    slideIndex: -1,
    anchorEl: null,
  });
  const { state: expandedAccordions, addItem: expandAccordion, removeItem: collapseAccordion } = useSetState<number>();
  const [unassignedSlides, setUnassignedSlides] = useState<number[]>([]);
  const [slidesMissingDescription, setSlidesMissingDescription] = useState<number[]>([]);
  const [isGeneratingAllMissingData, setIsGeneratingAllMissingData] = useState(false);
  const [elementMenuAnchor, setElementMenuAnchor] = useState<{ element: HTMLElement | null; slideIndex: number; elementName: string }>({
    element: null,
    slideIndex: -1,
    elementName: '',
  });
  const [focusedElement, setFocusedElement] = useState<{ slideIndex: number; elementName: string } | null>(null);
  const [textEditDialog, setTextEditDialog] = useState<{ open: boolean; slideIndex: number; elementName: string; currentValue: string }>({
    open: false,
    slideIndex: -1,
    elementName: '',
    currentValue: '',
  });
  const [rawContentEditDialog, setRawContentEditDialog] = useState<{ open: boolean; slideIndex: number; currentValue: string }>({
    open: false,
    slideIndex: -1,
    currentValue: '',
  });
  const [imagePicker, setImagePicker] = useState<{ open: boolean; slideIndex: number; elementName: string }>({
    open: false,
    slideIndex: -1,
    elementName: '',
  });
  const [imageGenerationDialog, setImageGenerationDialog] = useState<{ open: boolean; slideIndex: number; elementName: string }>({
    open: false,
    slideIndex: -1,
    elementName: '',
  });

  // Use useCallback for stability if passed as prop or dependency
  const getMissingDataSlideIndices = useCallback(() => {
    if (!pptxFile) return [];
    return editingSlides
      .map((slide, index) => {
        if (!slide.pptxSlideNumber) return -1; // Skip slides without template

        const parsedSlide = pptxFile.parsedSlides?.[slide.pptxSlideNumber - 1];
        if (!parsedSlide) return -1; // Skip if parsed slide data isn't available

        const hasMissingData = parsedSlide.elements.some((element: ParsedSlide['elements'][number]) => {
          // Check for missing data ONLY if not effectively locked
          return !isElementLocked(element, slide) && !slide.formData?.[element.name];
        });

        return hasMissingData ? index : -1;
      })
      .filter((index) => index !== -1);
  }, [editingSlides, pptxFile]); // Dependencies for useCallback

  // Update useEffect to use our utility functions
  useEffect(() => {
    setUnassignedSlides(getUnassignedSlides(editingSlides));
    setSlidesMissingDescription(getSlidesMissingDescription(editingSlides));
  }, [editingSlides]);

  const branding = useBranding();
  const { features } = branding;
  const enableImageGeneration = features?.enableImageGeneration ?? false;
  const handleUpdateSlide = (index: number, updates: Partial<Slide>) => {
    const newSlides = [...editingSlides];
    newSlides[index] = {
      ...newSlides[index],
      ...updates,
    };
    setEditingSlides(newSlides);
    onUpdateSlides(newSlides);
  };

  const handleAddSlide = () => {
    setNewSlideDialogOpen(true);
  };

  const handleAddSlideAtPosition = (position: 'above' | 'below', index: number) => {
    setNewSlideDialogOpen(true);
    setAddSlideTarget({ position, index });
  };

  const handleCreateNewSlide = (description: string) => {
    const newSlide = createNewSlide(description);
    const targetIndex = addSlideTarget
      ? addSlideTarget.position === 'above'
        ? addSlideTarget.index
        : addSlideTarget.index + 1
      : editingSlides.length;

    addSlide(
      newSlide,
      addSlideTarget
        ? {
            type: addSlideTarget.position,
            index: addSlideTarget.index,
          }
        : undefined
    );

    setAddSlideTarget(null);
    setNewSlideDialogOpen(false);

    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      const slideElements = document.querySelectorAll('.slides-editor > .MuiPaper-root');
      const targetSlide = slideElements[targetIndex];
      if (targetSlide) {
        targetSlide.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const handleRemoveSlide = (index: number) => {
    setDeleteConfirmation({
      open: true,
      slideIndex: index,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.slideIndex === null) return;
    removeSlide(deleteConfirmation.slideIndex);
  };

  const handleOpenBasicEditDialog = (index: number) => {
    addEditingDescription(index);
  };

  const handleCloseBasicEditDialog = (index: number) => {
    removeEditingDescription(index);
  };

  const handleSaveBasicEdit = (index: number, contentDescription: string) => {
    handleUpdateSlide(index, {
      contentDescription,
      aiSuggestionReasonForContentDescription: undefined, // Clear the reason when manually edited
    });
    handleCloseBasicEditDialog(index);
  };

  const handleOpenChangeTemplateDialog = (slideIndex: number) => {
    setPickingTemplateForSlideIndex(slideIndex);
  };

  const handleChangePptxSlideNumber = (slideIndex: number, pptxSlideNumber: number) => {
    handleUpdateSlide(slideIndex, { pptxSlideNumber });
  };

  const handleSuggestPptxSlideNumber = async (index: number) => {
    try {
      addSuggestingPptxSlideNumber(index);
      const slideNumber = index + 1;
      const result = await onSuggestPptxSlideNumber(slideNumber);
      if (result.slideNumber !== null && result.slideNumber !== undefined) {
        handleUpdateSlide(index, {
          pptxSlideNumber: result.slideNumber,
          aiSuggestionReasonForPptxSlideNumber: result.reason,
        });
      }
    } catch (error) {
      console.error('Error suggesting template:', error);
    } finally {
      removeSuggestingPptxSlideNumber(index);
    }
  };

  const handleSuggestSlideData = async (index: number, event: React.MouseEvent<HTMLElement>) => {
    setGenerateContentDialog({
      open: true,
      slideIndex: index,
      anchorEl: event.currentTarget,
    });
  };

  // Updated to filter results based on locked elements
  const handleGenerateContent = async (instruction: string, fileIds?: string[]) => {
    const slideIndex = generateContentDialog.slideIndex;
    try {
      addSuggestingSlideData(slideIndex);

      const currentSlide = editingSlides[slideIndex];
      if (!currentSlide || !currentSlide.pptxSlideNumber || !pptxFile) return;

      const slideData = await onSuggestSlideData(slideIndex, instruction, fileIds);

      // Filter out data for locked elements before updating
      const filteredFormData = { ...currentSlide.formData }; // Start with existing data
      const parsedSlide = pptxFile.parsedSlides?.[currentSlide.pptxSlideNumber - 1];
      if (parsedSlide) {
        Object.keys(slideData).forEach((elementName) => {
          const element = parsedSlide.elements.find((e) => e.name === elementName);
          if (element && !isElementLocked(element, currentSlide)) {
            filteredFormData[elementName] = slideData[elementName];
          } else if (!element) {
            // If element not found in template, maybe still add?
            filteredFormData[elementName] = slideData[elementName];
          }
        });
      } else {
        // If no parsed slide, apply all data (or handle error)
        Object.assign(filteredFormData, slideData);
      }

      handleUpdateSlide(slideIndex, {
        formData: filteredFormData,
      });

      expandAccordion(slideIndex);
    } catch (error) {
      console.error('Error requesting slide data:', error);
    } finally {
      removeSuggestingSlideData(slideIndex);
    }
  };

  // Add useEffect to sync props with local state
  useEffect(() => {
    if (JSON.stringify(slides) !== JSON.stringify(editingSlides)) {
      setEditingSlides(slides);
    }
  }, [slides]);

  const handleAccordionChange = (index: number, expanded: boolean) => {
    if (expanded) {
      expandAccordion(index);
    } else {
      collapseAccordion(index);
      // Clear focused element if this slide's accordion is being collapsed
      if (focusedElement?.slideIndex === index) {
        setFocusedElement(null);
      }
    }
  };

  // Updated to filter generated data based on locked elements
  const handleGenerateAllMissingData = async () => {
    setIsGeneratingAllMissingData(true);
    try {
      const slidesToProcessIndices = getMissingDataSlideIndices(); // Use the updated function
      if (slidesToProcessIndices.length === 0) return;

      slidesToProcessIndices.forEach((slideIndex) => {
        addSuggestingSlideData(slideIndex);
      });

      const updatedSlides = [...editingSlides];

      const generateTasks = slidesToProcessIndices
        .map((slideIndex) => {
          const slide = editingSlides[slideIndex];
          if (!slide.pptxSlideNumber || !pptxFile) return null;

          return async () => {
            const slideData = await onSuggestSlideData(slideIndex, undefined, undefined);
            // Filter out data for locked elements before creating the result
            const filteredFormData = { ...slide.formData };
            const parsedSlide = pptxFile.parsedSlides?.[slide.pptxSlideNumber! - 1];
            if (parsedSlide) {
              Object.keys(slideData).forEach((elementName) => {
                const element = parsedSlide.elements.find((e) => e.name === elementName);
                if (element && !isElementLocked(element, slide)) {
                  filteredFormData[elementName] = slideData[elementName];
                } else if (!element) {
                  filteredFormData[elementName] = slideData[elementName];
                }
              });
            } else {
              Object.assign(filteredFormData, slideData);
            }

            return {
              slideIndex,
              formData: filteredFormData, // Use the filtered data
            };
          };
        })
        .filter((task): task is () => Promise<{ slideIndex: number; formData: Record<string, any> }> => task !== null);

      // Use concurrency limit from branding context or default to 2
      const concurrencyLimit = branding.limits?.concurrentModelRequest || 2;

      const generator = processPromisesWithConcurrency(generateTasks, concurrencyLimit);

      // Process each result as soon as it's available
      for await (const result of generator) {
        if (result) {
          // Update the slide data
          updatedSlides[result.slideIndex] = {
            ...updatedSlides[result.slideIndex],
            formData: result.formData,
          };

          // Expand accordion immediately
          expandAccordion(result.slideIndex);

          // Update the UI state to reflect changes immediately
          setEditingSlides([...updatedSlides]);
        }
      }

      // Final update with all slides
      onUpdateSlides(updatedSlides);
    } catch (error) {
      console.error('Error generating missing data:', error);
    } finally {
      // Use the same logic to clear loading states
      getMissingDataSlideIndices().forEach((slideIndex) => {
        removeSuggestingSlideData(slideIndex);
      });
      setIsGeneratingAllMissingData(false);
    }
  };

  const handleFixUnassignedSlides = async () => {
    if (unassignedSlides.length === 0) return;

    setFixingUnassignedSlides({ isFixing: true, progress: 0, total: unassignedSlides.length });

    try {
      // Show loading indicators for all unassigned slides
      unassignedSlides.forEach((index) => {
        addSuggestingPptxSlideNumber(index);
      });

      // Use batch suggestion if available, otherwise fallback to individual calls
      if (onSuggestPptxSlideNumbers) {
        try {
          // Convert 0-based indices to 1-based slide numbers for API
          const slideNumbers = unassignedSlides.map((index) => index + 1);

          console.log('Making batch template suggestion request for slides:', slideNumbers);
          const templateSlideNumbers = await onSuggestPptxSlideNumbers(slideNumbers);

          // Update progress to show completion
          setFixingUnassignedSlides((prev) => ({
            ...prev,
            progress: prev.total,
          }));

          // Apply batch results to slides
          const updatedSlides = [...editingSlides];
          let hasUpdates = false;

          unassignedSlides.forEach((slideIndex, i) => {
            const templateNumber = templateSlideNumbers[i];
            if (templateNumber && templateNumber > 0) {
              updatedSlides[slideIndex] = {
                ...updatedSlides[slideIndex],
                pptxSlideNumber: templateNumber,
                aiSuggestionReasonForPptxSlideNumber: lang === 'en' ? 'Batch AI suggestion' : '批量 AI 建议',
              };
              hasUpdates = true;
            }
          });

          // Single update to avoid concurrent conflicts and persist to project
          if (hasUpdates) {
            setEditingSlides(updatedSlides);
            onUpdateSlides(updatedSlides);
          }
        } catch (batchError) {
          console.error('Batch template suggestion failed, falling back to individual calls:', batchError);

          // Fallback to individual API calls
          await handleFixUnassignedSlidesIndividual();
        }
      } else {
        // Fallback to individual calls if batch function not available
        await handleFixUnassignedSlidesIndividual();
      }
    } finally {
      // Remove loading indicators
      unassignedSlides.forEach((index) => {
        removeSuggestingPptxSlideNumber(index);
      });
      setFixingUnassignedSlides({ isFixing: false, progress: 0, total: 0 });
    }
  };

  // Fallback function for individual API calls (original logic)
  const handleFixUnassignedSlidesIndividual = async () => {
    // Create promises for all slide suggestions
    const suggestionPromises = unassignedSlides.map(async (index, promiseIndex) => {
      try {
        const slideNumber = index + 1;
        const result = await onSuggestPptxSlideNumber(slideNumber);

        // Update progress
        setFixingUnassignedSlides((prev) => ({
          ...prev,
          progress: prev.progress + 1,
        }));

        return {
          index,
          success: true,
          result:
            result.slideNumber !== null && result.slideNumber !== undefined
              ? {
                  pptxSlideNumber: result.slideNumber,
                  aiSuggestionReasonForPptxSlideNumber: result.reason,
                }
              : null,
        };
      } catch (error) {
        console.error(`Error suggesting template for slide ${index + 1}:`, error);
        setFixingUnassignedSlides((prev) => ({
          ...prev,
          progress: prev.progress + 1,
        }));
        return {
          index,
          success: false,
          error,
        };
      }
    });

    // Wait for all promises to complete
    const results = await Promise.all(suggestionPromises);

    // Batch update all successful results
    const updatedSlides = [...editingSlides];
    let hasUpdates = false;

    results.forEach(({ index, success, result }) => {
      if (success && result) {
        updatedSlides[index] = {
          ...updatedSlides[index],
          ...result,
        };
        hasUpdates = true;
      }
    });

    // Single update to avoid concurrent conflicts and persist to project
    if (hasUpdates) {
      setEditingSlides(updatedSlides);
      onUpdateSlides(updatedSlides); // Add onUpdateSlides call
    }
  };

  const handleElementMenuOpen = (event: React.MouseEvent<HTMLElement>, slideIndex: number, elementName: string) => {
    event.stopPropagation();
    setElementMenuAnchor({
      element: event.currentTarget,
      slideIndex,
      elementName,
    });
  };

  const handleElementMenuClose = () => {
    setElementMenuAnchor({ element: null, slideIndex: -1, elementName: '' });
  };

  // Updated lock/unlock logic
  const handleToggleElementLock = (slideIndex: number, elementName: string) => {
    const slide = editingSlides[slideIndex];
    const element = pptxFile?.parsedSlides?.[slide.pptxSlideNumber! - 1]?.elements.find((e) => e.name === elementName);
    if (!element) return; // Element not found

    const isDefaultLocked = element.isLocked === true;
    const currentOverrideList = slide.lockedElementNames || [];
    const isInOverrideList = currentOverrideList.includes(elementName);
    const effectivelyLocked = isElementLocked(element, slide); // Use helper

    let updatedOverrideList: string[];

    // If currently effectively locked, toggling means unlocking.
    if (effectivelyLocked) {
      // Unlock action
      if (isDefaultLocked) {
        // Add to override list to signify unlocking a default lock
        updatedOverrideList = [...currentOverrideList, elementName];
      } else {
        // Remove from override list to signify unlocking a user lock
        updatedOverrideList = currentOverrideList.filter((name) => name !== elementName);
      }
    } else {
      // Lock action
      if (isDefaultLocked) {
        // Remove from override list to signify re-locking (back to default)
        updatedOverrideList = currentOverrideList.filter((name) => name !== elementName);
      } else {
        // Add to override list to signify locking an unlocked default
        updatedOverrideList = [...currentOverrideList, elementName];
      }
    }

    // Remove duplicates just in case
    updatedOverrideList = Array.from(new Set(updatedOverrideList));

    handleUpdateSlide(slideIndex, {
      lockedElementNames: updatedOverrideList,
    });
    handleElementMenuClose();
  };

  // Callback for generating single element content (moved from GenerateContentDialog)
  const handleGenerateElementContent = async (
    event: React.MouseEvent<HTMLElement>,
    slideIndex: number,
    elementIndex: number,
    elementName: string,
    elementType: 'text' | 'picture' | 'table' | 'chart',
    fromPreview: boolean = false
  ) => {
    // Expand the accordion for this slide to show the element list
    expandAccordion(slideIndex);

    // Set the focused element
    setFocusedElement({ slideIndex, elementName });

    // Set up the dialog using the existing state setter
    setGenerateContentDialog({
      open: true,
      slideIndex,
      elementIndex,
      elementName,
      elementType,
      anchorEl: event.currentTarget,
    });

    if (!fromPreview) {
      // Scroll to the element after a brief delay to ensure accordion is expanded
      setTimeout(() => {
        const elementId = `slide-element-${slideIndex}-${elementIndex}`;
        const el = document.getElementById(elementId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleGenerateSlideDescription = async (index: number) => {
    if (!onRecommendSlideDescription) return;

    try {
      addGeneratingDescription(index);
      const recommendation = await onRecommendSlideDescription(index);

      const newSlides = [...editingSlides];
      newSlides[index] = {
        ...newSlides[index],
        contentDescription: recommendation.description,
        aiSuggestionReasonForContentDescription: recommendation.reasonForRecommendation,
      };

      setEditingSlides(newSlides);
      onUpdateSlides(newSlides);
    } catch (error) {
      console.error('Error generating slide description:', error);
    } finally {
      removeGeneratingDescription(index);
    }
  };

  // Add a function to clear element value
  const handleClearElementValue = (slideIndex: number, elementName: string) => {
    const slide = editingSlides[slideIndex];
    // Only proceed if the element has a value to clear
    if (!slide.formData || !slide.formData[elementName]) {
      handleElementMenuClose();
      return;
    }

    // Create a new formData object without the specified element
    const updatedFormData = { ...slide.formData };
    delete updatedFormData[elementName];

    // Update the slide
    handleUpdateSlide(slideIndex, {
      formData: updatedFormData,
    });
    handleElementMenuClose();
  };

  // Handle opening text edit dialog
  const handleEditText = (slideIndex: number, elementName: string) => {
    const slide = editingSlides[slideIndex];
    const currentValue = slide.formData?.[elementName] || '';

    setTextEditDialog({
      open: true,
      slideIndex,
      elementName,
      currentValue,
    });
    handleElementMenuClose();
  };

  // Handle saving text edit
  const handleSaveTextEdit = (newValue: string) => {
    const { slideIndex, elementName } = textEditDialog;
    const slide = editingSlides[slideIndex];

    handleUpdateSlide(slideIndex, {
      formData: {
        ...slide.formData,
        [elementName]: newValue,
      },
    });

    setTextEditDialog({
      open: false,
      slideIndex: -1,
      elementName: '',
      currentValue: '',
    });
  };

  // Handle opening raw content edit dialog
  const handleOpenRawContentEditDialog = (slideIndex: number) => {
    const slide = editingSlides[slideIndex];
    const currentValue = slide.rawContent || '';

    setRawContentEditDialog({
      open: true,
      slideIndex,
      currentValue,
    });
  };

  // Handle saving raw content edit
  const handleSaveRawContentEdit = (newValue: string) => {
    const { slideIndex } = rawContentEditDialog;

    handleUpdateSlide(slideIndex, {
      rawContent: newValue,
    });

    setRawContentEditDialog({
      open: false,
      slideIndex: -1,
      currentValue: '',
    });
  };

  // Handle opening image replace dialog
  const handleReplaceImage = (slideIndex: number, elementName: string) => {
    setImagePicker({ open: true, slideIndex, elementName });
    handleElementMenuClose();
  };

  // Handle selecting image for element
  const handleSelectImageForElement = (image: Image) => {
    const { slideIndex, elementName } = imagePicker;
    if (slideIndex < 0 || !elementName) return;
    const slide = editingSlides[slideIndex];
    handleUpdateSlide(slideIndex, {
      formData: {
        ...slide.formData,
        [elementName]: image.url,
      },
    });

    setImagePicker({ open: false, slideIndex: -1, elementName: '' });
  };

  // Pre-calculate missing data indices for the render phase
  const missingDataIndices = getMissingDataSlideIndices();

  const handleGenerateImage = (slideIndex: number, elementName: string) => {
    setImageGenerationDialog({ open: true, slideIndex, elementName });
    handleElementMenuClose();
  };

  const handleCloseGenerateImageDialog = () => {
    setImageGenerationDialog({ open: false, slideIndex: -1, elementName: '' });
  };

  const handleGenerateImageSuccess = ({ url }: { url: string; _id: string }) => {
    const { slideIndex, elementName } = imageGenerationDialog;
    if (slideIndex < 0 || !elementName) return;
    const slide = editingSlides[slideIndex];
    handleUpdateSlide(slideIndex, {
      formData: {
        ...slide.formData,
        [elementName]: url,
      },
    });
    handleCloseGenerateImageDialog();
  };

  // Handle hover events coming from SlidePreviewWithBoundaries
  const handlePreviewElementHover = useCallback(
    (slideIdx: number, elementIdx: number | null) => {
      if (elementIdx !== null) {
        setHoveredElement({ slideIndex: slideIdx, elementIndex: elementIdx.toString() });
        // Ensure the accordion is expanded so the element list is visible
        expandAccordion(slideIdx);
        // Scroll the hovered element into view
        requestAnimationFrame(() => {
          const el = document.getElementById(`slide-element-${slideIdx}-${elementIdx}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      } else {
        // Clear hover when leaving element
        setHoveredElement((prev) => (prev && prev.slideIndex === slideIdx ? null : prev));
      }
    },
    [expandAccordion]
  );

  // console.log('newSlideDialogOpen:', newSlideDialogOpen);
  return (
    <Box className="slides-editor" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {slidesMissingDescription.length > 0 && (
        <Alert severity="error" variant="filled">
          <AlertTitle>缺少内容描述</AlertTitle>
          幻灯片 {slidesMissingDescription.map((index) => index + 1).join(', ')} 缺少内容描述。请为它们添加描述。
        </Alert>
      )}

      {/* Use pre-calculated missingDataIndices */}
      {missingDataIndices.length > 0 && (
        <Alert
          variant="filled"
          severity="warning"
          action={
            <Button
              sx={{ minWidth: 120 }}
              variant="outlined"
              color="inherit"
              onClick={handleGenerateAllMissingData}
              disabled={isGeneratingAllMissingData}
              startIcon={isGeneratingAllMissingData ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            >
              {lang === 'en' ? 'Generate All' : '生成全部幻灯片'}
            </Button>
          }
        >
          <AlertTitle>{lang === 'en' ? 'Missing Slide Data' : '缺少幻灯片数据'}</AlertTitle>
          {lang === 'en'
            ? `Slides ${missingDataIndices
                .map((index) => index + 1)
                .join(', ')} have empty elements (excluding locked ones). Click "Generate All" to generate content for all missing elements.`
            : `幻灯片 ${missingDataIndices
                .map((index) => index + 1)
                .join(', ')} 有空白元素（已排除锁定的元素）。点击"生成所有"以自动生成所有缺失的内容。`}
        </Alert>
      )}

      {unassignedSlides.length > 0 && (
        <Alert
          variant="outlined"
          severity={fixingUnassignedSlides.isFixing ? 'info' : 'warning'}
          action={
            <Button
              onClick={handleFixUnassignedSlides}
              startIcon={fixingUnassignedSlides.isFixing ? <CircularProgress size={16} color="inherit" /> : <Build />}
              disabled={fixingUnassignedSlides.isFixing}
            >
              {fixingUnassignedSlides.isFixing ? (lang === 'en' ? 'Fixing...' : '修复中...') : lang === 'en' ? 'Fix All' : '修复所有'}
            </Button>
          }
        >
          <AlertTitle>
            {fixingUnassignedSlides.isFixing
              ? lang === 'en'
                ? 'Fixing Unassigned Slides'
                : '正在修复未分配的幻灯片'
              : lang === 'en'
              ? 'Unassigned Slides'
              : '未分配的幻灯片'}
          </AlertTitle>
          {fixingUnassignedSlides.isFixing ? (
            <div>
              {lang === 'en'
                ? `Processing ${fixingUnassignedSlides.progress} of ${fixingUnassignedSlides.total} slides...`
                : `正在处理第 ${fixingUnassignedSlides.progress} / ${fixingUnassignedSlides.total} 张幻灯片...`}
            </div>
          ) : (
            <div>
              {lang === 'en'
                ? `Slides ${unassignedSlides
                    .map((index) => index + 1)
                    .join(', ')} are not assigned to templates. Click "Fix All" to automatically recommend templates for them.`
                : `幻灯片 ${unassignedSlides.map((index) => index + 1).join(', ')} 没有分配模板。点击"修复所有"以自动为它们推荐模板。`}
            </div>
          )}
        </Alert>
      )}

      {editingSlides.length === 0 && (
        <Stack sx={{ justifyContent: 'center', alignItems: 'center', mt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {lang === 'en' ? 'You can manually add slides.' : '你可以手动添加幻灯片。'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 2,
            }}
            component="div"
          >
            <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={handleAddSlide}>
              {lang === 'en' ? 'Add Slide' : '添加幻灯片'}
            </Button>
          </Box>
        </Stack>
      )}

      {editingSlides.map((slide, index) => {
        const currentPptxSlide = slide.pptxSlideNumber && pptxFile ? pptxFile.parsedSlides[slide.pptxSlideNumber - 1] : undefined;
        const isElementGenerating = Array.from(elementSuggestingStates).some((key) => key.startsWith(`${index}-`));
        const isSlideGenerating = slidesWithSuggestingSlideData.has(index);
        const isGenerating = isSlideGenerating || isElementGenerating;

        return (
          <Paper
            key={index}
            sx={{
              position: 'relative',
              p: 2,
              boxShadow: currentSelectedSlideIndex === index ? 5 : 0,
              transition: 'all 0.3s ease-in-out',
            }}
            onClick={() => {
              setCurrentSelectedSlideIndex(index);
              // Clear focused element when clicking on a different slide
              if (focusedElement && focusedElement.slideIndex !== index) {
                setFocusedElement(null);
              }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                position: 'absolute',
                top: 8,
                left: '-1.5rem',
                fontWeight: 'bold',
              }}
              color="text.secondary"
            >
              {index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SlideActions
                  index={index}
                  totalSlides={editingSlides.length}
                  onMoveSlide={moveSlide}
                  onRemoveSlide={handleRemoveSlide}
                  onAddSlide={handleAddSlideAtPosition}
                  lang={lang}
                />
                {currentPptxSlide ? (
                  <SlidePreviewWithBoundaries
                    fileId={project.pptxFileId}
                    slideNumber={slide.pptxSlideNumber!}
                    slide={{
                      ...currentPptxSlide,
                      elements: currentPptxSlide.elements.map((elem) => ({
                        ...elem,
                        valuePresent:
                          slide.formData &&
                          slide.formData[elem.name] !== undefined &&
                          slide.formData[elem.name] !== '' &&
                          slide.formData[elem.name] !== null,
                      })),
                    }}
                    isLoading={slidesWithSuggestingPptxSlideNumber.has(index)}
                    hoveredElementId={hoveredElement?.slideIndex === index ? hoveredElement.elementIndex : undefined}
                    slideWidth={pptxFile?.slideWidth ? pptxFile.slideWidth * 914400 : undefined}
                    slideHeight={pptxFile?.slideHeight ? pptxFile.slideHeight * 914400 : undefined}
                    isElementLocked={(elementIndex) => {
                      const element = currentPptxSlide.elements[elementIndex];
                      return isElementLocked(element, slide);
                    }}
                    onElementHover={(hoverIdx) => handlePreviewElementHover(index, hoverIdx)}
                    onClick={(elementIndices, defaultIndex, event) => {
                      // If no elements were clicked, do nothing
                      if (elementIndices.length === 0 || defaultIndex === null) {
                        return;
                      }

                      // Get the default (smallest) element
                      const defaultElement = currentPptxSlide.elements[defaultIndex];
                      const isLocked = isElementLocked(defaultElement, slide);

                      // If the default element is locked, do nothing
                      if (isLocked) {
                        return;
                      }

                      // Trigger content generation for the default element
                      handleGenerateElementContent(
                        event as React.MouseEvent<HTMLElement>,
                        index, // slideIndex
                        defaultIndex, // elementIndex
                        defaultElement.name,
                        defaultElement.type as 'text' | 'picture' | 'table' | 'chart',
                        true
                      );
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                      position: 'relative',
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        gap: 2,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary" align="center" sx={{ wordBreak: 'break-word' }}>
                        {slide.contentDescription || (lang === 'en' ? 'No template assigned' : '未分配模板')}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSuggestPptxSlideNumber(index);
                          }}
                          {...magicButtonProps({
                            loading: slidesWithSuggestingPptxSlideNumber.has(index),
                          })}
                        >
                          {lang === 'en' ? 'Suggest Template' : '推荐模板'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPickingTemplateForSlideIndex(index);
                          }}
                        >
                          {lang === 'en' ? 'Choose Template' : '选择模板'}
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  maxHeight: 'calc(100vh - 144px)',
                  minHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                <List dense>
                  {/* Raw Content Section - Only shown for verbatim material projects */}
                  {project.verbatimMaterialId && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => handleOpenRawContentEditDialog(index)}>
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" gap={1}>
                                {lang === 'en' ? 'Raw Content' : '原始内容'}
                              </Stack>
                            }
                            secondary={slide.rawContent || (lang === 'en' ? '(empty)' : '（空）')}
                            secondaryTypographyProps={{
                              sx: {
                                whiteSpace: 'pre-line',
                                maxHeight: '100px',
                                overflow: 'auto',
                                display: 'block',
                              },
                            }}
                          />
                        </ListItemButton>
                        <ListItemSecondaryAction>
                          <Tooltip title={lang === 'en' ? 'Edit Raw Content' : '编辑原始内容'}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenRawContentEditDialog(index);
                              }}
                              color="primary"
                              edge="end"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleOpenBasicEditDialog(index)}>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={1}>
                            {lang === 'en'
                              ? project.verbatimMaterialId
                                ? 'Description of layout of current slide'
                                : 'Description of current slide'
                              : project.verbatimMaterialId
                              ? '此页布局描述'
                              : '此页描述'}
                            {slidesMissingDescription.includes(index) && <ErrorOutline color="error" fontSize="small" />}
                            {slide.aiSuggestionReasonForContentDescription && (
                              <Tooltip
                                title={slide.aiSuggestionReasonForContentDescription}
                                arrow
                                placement="top-start"
                                enterTouchDelay={0}
                                leaveTouchDelay={5000}
                              >
                                <HelpOutline color="info" fontSize="small" />
                              </Tooltip>
                            )}
                          </Stack>
                        }
                        secondary={slide.contentDescription}
                      />
                    </ListItemButton>
                    <ListItemSecondaryAction>
                      <Tooltip title={lang === 'en' ? 'AI Generate Description' : 'AI生成描述'}>
                        <span style={{ zIndex: 1 }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateSlideDescription(index);
                            }}
                            disabled={!onRecommendSlideDescription || slidesWithGeneratingDescription.has(index)}
                            color="primary"
                            edge="end"
                          >
                            {slidesWithGeneratingDescription.has(index) ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <AutoAwesome fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleOpenChangeTemplateDialog(index)}>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={1}>
                            {lang === 'en' ? (slide.pptxSlideNumber ? 'Change Template' : 'Template') : slide.pptxSlideNumber ? '更换模板' : '模板'}{' '}
                            {!slide.pptxSlideNumber && <Warning color="warning" fontSize="inherit" />}
                            {slide.aiSuggestionReasonForPptxSlideNumber && (
                              <Tooltip
                                title={slide.aiSuggestionReasonForPptxSlideNumber}
                                arrow
                                placement="top-start"
                                enterTouchDelay={0}
                                leaveTouchDelay={5000}
                              >
                                <HelpOutline color="info" fontSize="small" />
                              </Tooltip>
                            )}
                          </Stack>
                        }
                        secondary={
                          slide.pptxSlideNumber ? `Template ${slide.pptxSlideNumber}` : lang === 'en' ? 'No template assigned' : '未分配模板'
                        }
                      />
                    </ListItemButton>
                    <ListItemSecondaryAction>
                      <Tooltip title={lang === 'en' ? 'Suggest Template' : '推荐模版'}>
                        <span>
                          <IconButton
                            onClick={() => handleSuggestPptxSlideNumber(index)}
                            disabled={slidesWithSuggestingPptxSlideNumber.has(index)}
                            // @ts-ignore
                            color="ai"
                            edge="end"
                          >
                            {slidesWithSuggestingPptxSlideNumber.has(index) ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <AutoAwesome fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <Accordion
                    elevation={0}
                    expanded={expandedAccordions.has(index)}
                    onChange={(_, expanded) => handleAccordionChange(index, expanded)}
                  >
                    <AccordionSummary
                      aria-controls="slide-elements-content"
                      id="slide-elements-header"
                      sx={{
                        '& .MuiAccordionSummary-content': { margin: 0 },
                        '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body1">{lang === 'en' ? 'Slide Elements' : '幻灯片元素'}</Typography>
                            {currentPptxSlide &&
                              (() => {
                                const elementCounts = currentPptxSlide
                                  ? currentPptxSlide.elements.reduce(
                                      (acc, element) => {
                                        if (!isElementLocked(element, slide)) {
                                          acc.total++;
                                          if (slide.formData && slide.formData[element.name]) {
                                            acc.completed++;
                                          }
                                        }
                                        return acc;
                                      },
                                      { completed: 0, total: 0 }
                                    )
                                  : { completed: 0, total: 0 };
                                const isIncomplete = elementCounts.completed < elementCounts.total;
                                return (
                                  <Typography variant="body2" color={isIncomplete ? 'warning.main' : 'text.secondary'}>
                                    ({elementCounts.completed}/{elementCounts.total})
                                  </Typography>
                                );
                              })()}
                            {!slide.pptxSlideNumber && (
                              <Typography variant="body2" color="warning.main">
                                {lang === 'en' ? 'Select a template first' : '请先选择模板'}
                              </Typography>
                            )}
                          </Stack>
                          <ExpandMore
                            sx={{
                              transform: expandedAccordions.has(index) ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s',
                            }}
                          />
                        </Stack>
                        <Tooltip title={lang === 'en' ? 'Generate All Elements' : '生成所有元素'}>
                          <span style={{ zIndex: 1 }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSuggestSlideData(index, e);
                              }}
                              disabled={!slide.pptxSlideNumber || isGenerating}
                              color="primary"
                              edge="end"
                            >
                              {isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ px: 1, py: 0, bgcolor: 'background.default', borderRadius: 2, boxShadow: 'inset 0 0 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      {currentPptxSlide ? (
                        <SlideElementList
                          slide={slide}
                          pptxSlide={currentPptxSlide}
                          slideIndex={index}
                          lang={lang}
                          elementSuggestingStates={elementSuggestingStates}
                          focusedElementName={focusedElement?.slideIndex === index ? focusedElement.elementName : undefined}
                          hoveredElementIndex={hoveredElement?.slideIndex === index ? Number(hoveredElement.elementIndex) : null}
                          onOpenElementMenu={handleElementMenuOpen}
                          onGenerateElementContent={handleGenerateElementContent}
                          onElementMouseEnter={(elementIndex) => setHoveredElement({ slideIndex: index, elementIndex: elementIndex.toString() })}
                          onElementMouseLeave={() => setHoveredElement(null)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {lang === 'en' ? 'Assign a template slide first.' : '请先分配模板幻灯片。'}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </List>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <Fab color="primary" aria-label="add" onClick={handleAddSlide} sx={{ position: 'fixed', bottom: 16, right: [16, 32] }}>
        <AddIcon />
      </Fab>

      {/* Basic Edit Dialog */}
      {Array.from(editingDescriptionSlideIndices).map((index) => (
        <BasicEditDialog
          key={index}
          open
          slide={editingSlides[index]}
          onClose={() => handleCloseBasicEditDialog(index)}
          onSave={(contentDescription) => handleSaveBasicEdit(index, contentDescription)}
        />
      ))}

      {/* Change Template Dialog */}
      <ChangeTemplateDialog
        open={pickingTemplateForSlideIndex !== null}
        onClose={() => setPickingTemplateForSlideIndex(null)}
        onPick={(slideNumber) => {
          if (pickingTemplateForSlideIndex !== null) {
            handleChangePptxSlideNumber(pickingTemplateForSlideIndex, slideNumber);
          }
          setPickingTemplateForSlideIndex(null);
        }}
        pptxFileId={project.pptxFileId}
        lang={lang}
      />

      {/* Updated Generate Content Dialog - Added lock check */}
      <GenerateContentDialog
        open={generateContentDialog.open}
        anchorEl={generateContentDialog.anchorEl}
        onClose={() => {
          setGenerateContentDialog({
            open: false,
            slideIndex: -1,
            anchorEl: null,
          });
          // Clear focused element when dialog closes
          setFocusedElement(null);
        }}
        onSubmit={async (instruction, fileIds) => {
          const { slideIndex, elementName, elementType } = generateContentDialog;

          if (elementName && elementType) {
            // Handle element-specific content generation
            const slide = editingSlides[slideIndex];
            const element = pptxFile?.parsedSlides?.[slide?.pptxSlideNumber! - 1]?.elements.find((e) => e.name === elementName);

            // Check if the element is effectively locked before proceeding
            if (element && isElementLocked(element, slide)) {
              console.log(`Element "${elementName}" in slide ${slideIndex + 1} is locked. Skipping generation.`);
              // Optionally show feedback to the user here
              return;
            }

            const stateKey = `${slideIndex}-${elementName}`;
            try {
              addElementSuggesting(stateKey);
              const suggestedValue = await onSuggestSlideElementValue(slideIndex, elementName, elementType, instruction || undefined, fileIds);
              handleUpdateSlide(slideIndex, {
                formData: {
                  ...slide.formData,
                  [elementName]: suggestedValue,
                },
              });
              // Expand accordion to show the newly generated content
              expandAccordion(slideIndex);

              // Scroll the updated element into view
              setTimeout(() => {
                const el = document.getElementById(`slide-element-${slideIndex}-${generateContentDialog.elementIndex}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 500);
            } catch (error) {
              console.error('Error suggesting value:', error);
            } finally {
              removeElementSuggesting(stateKey);
            }
          } else {
            // Handle general slide content generation (already updated above)
            await handleGenerateContent(instruction, fileIds);
          }
        }}
        title={
          generateContentDialog.elementName
            ? generateContentDialog.elementType === 'picture'
              ? lang === 'en'
                ? `Recommend Image for ${generateContentDialog.elementName}`
                : `为 ${generateContentDialog.elementName} 推荐图片`
              : lang === 'en'
              ? `Generate Content for ${generateContentDialog.elementName}`
              : `为 ${generateContentDialog.elementName} 生成内容`
            : lang === 'en'
            ? 'Generate Slide Content'
            : '生成幻灯片内容'
        }
        loading={
          generateContentDialog.elementName
            ? elementSuggestingStates.has(`${generateContentDialog.slideIndex}-${generateContentDialog.elementName}`)
            : slidesWithSuggestingSlideData.has(generateContentDialog.slideIndex)
        }
        elementType={generateContentDialog.elementType}
        lang={lang}
        preDefinedInstructions={preDefinedInstructions}
        placeholder={
          generateContentDialog.elementType === 'picture'
            ? lang === 'en'
              ? 'Provide additional instructions to help AI recommend more accurate images...'
              : '为 AI 提供额外的指令，以便更准确地推荐图片...'
            : undefined
        }
        projectId={project._id}
      />

      {/* New Slide Dialog */}
      <NewSlideDialog open={newSlideDialogOpen} onClose={() => setNewSlideDialogOpen(false)} onSubmit={handleCreateNewSlide} lang={lang} />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, slideIndex: null })}
        onConfirm={handleConfirmDelete}
        slideNumber={(deleteConfirmation.slideIndex ?? 0) + 1}
        lang={lang}
      />

      {/* Element Menu - Updated logic for lock/unlock text/icon */}
      <Menu anchorEl={elementMenuAnchor.element} open={Boolean(elementMenuAnchor.element)} onClose={handleElementMenuClose}>
        {/* Conditional rendering based on effective lock state */}
        {(() => {
          const slideIndex = elementMenuAnchor.slideIndex;
          const elementName = elementMenuAnchor.elementName;
          if (slideIndex < 0 || !elementName) return null; // Ensure valid state

          const slide = editingSlides[slideIndex];
          const element = pptxFile?.parsedSlides?.[slide?.pptxSlideNumber! - 1]?.elements.find((e) => e.name === elementName);

          if (!element) return null; // Element must exist

          const effectivelyLocked = isElementLocked(element, slide); // Use helper
          const hasValue = slide.formData && slide.formData[elementName]; // Check if element has a value

          const menuItems = [];

          // Edit text menu for textual elements
          if (element.type === 'shape' && !effectivelyLocked) {
            menuItems.push(
              <MenuItem key="edit-text" onClick={() => handleEditText(slideIndex, elementName)}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                {lang === 'en' ? 'Edit Text' : '编辑文字'}
              </MenuItem>
            );
          }

          // Replace image menu for picture elements
          if (element.type === 'picture' && !effectivelyLocked) {
            menuItems.push(
              <MenuItem key="replace-image" onClick={() => handleReplaceImage(slideIndex, elementName)}>
                <ImageIcon fontSize="small" sx={{ mr: 1 }} />
                {lang === 'en' ? 'Replace Image' : '替换图片'}
              </MenuItem>
            );
            // Add generate image menu when feature enabled
            if (enableImageGeneration) {
              menuItems.push(
                <MenuItem key="generate-image" onClick={() => handleGenerateImage(slideIndex, elementName)}>
                  <AutoAwesome fontSize="small" sx={{ mr: 1 }} />
                  {lang === 'en' ? 'Generate Image' : '生成图片'}
                </MenuItem>
              );
            }
          }

          // Lock/Unlock menu item
          menuItems.push(
            <MenuItem key="toggle-lock" onClick={() => handleToggleElementLock(slideIndex, elementName)}>
              {effectivelyLocked ? (
                <>
                  <LockOpenIcon fontSize="small" sx={{ mr: 1 }} />
                  {lang === 'en' ? 'Unlock Element' : '解锁元素'}
                </>
              ) : (
                <>
                  <LockIcon fontSize="small" sx={{ mr: 1 }} />
                  {lang === 'en' ? 'Lock Element' : '锁定元素'}
                </>
              )}
            </MenuItem>
          );

          // Clear value menu item
          if (!effectivelyLocked && hasValue) {
            menuItems.push(
              <MenuItem key="clear-value" onClick={() => handleClearElementValue(slideIndex, elementName)}>
                <ClearIcon fontSize="small" sx={{ mr: 1 }} />
                {lang === 'en' ? 'Clear Value' : '清除值'}
              </MenuItem>
            );
          }

          return menuItems;
        })()}
      </Menu>

      {textEditDialog.open && (
        <TextEditorDialog
          onClose={() => setTextEditDialog({ open: false, slideIndex: -1, elementName: '', currentValue: '' })}
          onSave={handleSaveTextEdit}
          initialValue={textEditDialog.currentValue}
        />
      )}

      {/* Raw Content Edit Dialog */}
      {rawContentEditDialog.open && (
        <TextEditorDialog
          onClose={() => setRawContentEditDialog({ open: false, slideIndex: -1, currentValue: '' })}
          onSave={handleSaveRawContentEdit}
          initialValue={rawContentEditDialog.currentValue}
          dialogProps={{ maxWidth: 'md' }}
          textFieldProps={{ multiline: true, rows: 10 }}
        />
      )}

      {/* Image Generation Dialog */}
      {enableImageGeneration && (
        <ImageGenerationDialog
          open={imageGenerationDialog.open}
          onClose={handleCloseGenerateImageDialog}
          onGenerateSuccess={handleGenerateImageSuccess}
        />
      )}

      {/* Image Picker for replacing element images */}
      <ImagePickerContainer
        open={imagePicker.open}
        onClose={() => setImagePicker({ open: false, slideIndex: -1, elementName: '' })}
        onSelectImage={handleSelectImageForElement}
        tabs={['Project', 'User', 'Public']}
        projectId={project._id}
      />
    </Box>
  );
};

export default SlidesEditor;
